/**
 * PayablesRecordDialog
 * --------------------
 * Manual entry for a supplier invoice or a payment — the way the Sourcing Desk
 * lets you key an order by hand. Used for records that were never captured:
 * attach the invoice PDF or the bank confirmation in the same pass.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, FileText, Banknote, Upload, Paperclip, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const METHODS = [
  { id: "wire", label: "Commercial wire" },
  { id: "ach", label: "ACH transfer" },
  { id: "check", label: "Check" },
  { id: "card", label: "Credit card" },
  { id: "cash", label: "Cash" },
  { id: "qbo_bill_pay", label: "QuickBooks bill pay" },
  { id: "other", label: "Other" },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

export const PayablesRecordDialog = ({ kind, record, token, defaultSupplier, invoices, onClose, onSaved }) => {
  const isInvoice = kind === "invoice";
  const editing = !!record;
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const fileInput = useRef(null);
  const [form, setForm] = useState(() => ({
    supplier_name: record?.supplier_name || defaultSupplier?.name || "",
    supplier_email: record?.supplier_email || "",
    invoice_number: record?.invoice_number || "",
    invoice_date: record?.invoice_date || "",
    due_date: record?.due_date || "",
    terms: record?.terms || "Net 30",
    amount: record?.amount != null ? String(record.amount) : "",
    status: record?.status || "received",
    method: record?.method || "ach",
    payment_date: record?.payment_date || new Date().toISOString().slice(0, 10),
    reference: record?.reference || "",
    bank_name: record?.bank_name || "",
    account_last4: record?.account_last4 || "",
    invoice_id: record?.invoice_id || "",
    notes: record?.notes || "",
    link_id: record?.link_id && record.link_id !== "standalone" ? record.link_id : "",
  }));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const headers = { Authorization: `Bearer ${token}` };

  const uploadTo = async (recId) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", isInvoice ? "invoice" : "payment_confirmation");
    await axios.post(`${API}/payables/${isInvoice ? "invoices" : "payments"}/${recId}/attachments`,
      fd, { headers });
  };

  const save = async (force = false) => {
    if (!form.supplier_name.trim()) return toast.error("Name the supplier or contractor");
    if (Number(form.amount) <= 0) return toast.error("Enter the amount");
    if (isInvoice && !form.invoice_number.trim()) return toast.error("Enter the invoice number");
    setSaving(true);
    try {
      const linked = form.link_id.trim();
      const link = linked
        ? { link_type: /^(WO|MR)/i.test(linked) ? "work_order" : "backorder", link_id: linked }
        : { link_type: "standalone", link_id: "standalone" };

      let recId = record?.id;
      if (isInvoice) {
        const body = {
          supplier_name: form.supplier_name.trim(),
          supplier_email: form.supplier_email.trim() || null,
          supplier_id: defaultSupplier?.id || record?.supplier_id || null,
          invoice_number: form.invoice_number.trim(),
          invoice_date: form.invoice_date || null,
          due_date: form.due_date || null,
          terms: form.terms || null,
          amount: Number(form.amount),
          status: form.status,
          notes: form.notes || null,
          force,
        };
        if (editing) {
          await axios.put(`${API}/payables/invoices/${record.id}`, body, { headers });
        } else {
          const r = await axios.post(`${API}/payables/invoices`, { ...body, ...link }, { headers });
          recId = r.data?.invoice?.id;
        }
      } else {
        const allocations = form.invoice_id
          ? [{ invoice_id: form.invoice_id, amount: Number(form.amount) }]
          : [];
        const body = {
          method: form.method,
          payment_date: form.payment_date || null,
          reference: form.reference || null,
          bank_name: form.bank_name || null,
          account_last4: form.account_last4 || null,
          notes: form.notes || null,
          force,
        };
        if (editing) {
          await axios.put(`${API}/payables/payments/${record.id}`,
            allocations.length
              ? { ...body, allocations }
              : { ...body, amount: Number(form.amount),
                  supplier_name: form.supplier_name.trim(),
                  supplier_email: form.supplier_email.trim() || null },
            { headers });
        } else {
          const r = await axios.post(`${API}/payables/payments`, {
            ...body, ...link, allocations,
            supplier_name: form.supplier_name.trim(),
            supplier_email: form.supplier_email.trim() || null,
            supplier_id: defaultSupplier?.id || null,
            amount: Number(form.amount),
          }, { headers });
          recId = r.data?.payment?.id;
        }
      }

      if (recId) await uploadTo(recId);
      if (editing && record?.link_id !== (linked || "standalone")) {
        await axios.post(`${API}/payables/${isInvoice ? "invoices" : "payments"}/${record.id}/link`,
          link, { headers });
      }
      toast.success(`${isInvoice ? "Invoice" : "Payment"} ${editing ? "updated" : "recorded"}`);
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code) {
        setDuplicate({ ...detail, kind: isInvoice ? "invoice" : "payment" });
      } else {
        toast.error(typeof detail === "string" ? detail
          : detail?.message || "Could not save that record");
      }
    } finally { setSaving(false); }
  };

  const openInvoices = (invoices || []).filter(i => (i.balance ?? i.amount) > 0.009);

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}
        data-testid="payables-record-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              {isInvoice ? <FileText className="w-5 h-5 text-slate-700" /> : <Banknote className="w-5 h-5 text-emerald-600" />}
              {editing ? "Edit" : "Enter"} supplier {isInvoice ? "invoice" : "payment"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              For orders that never made it into the system — key the record and attach the{" "}
              {isInvoice ? "invoice PDF" : "bank or check confirmation"}.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="payables-record-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Supplier / contractor">
              <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)}
                className="h-9 text-sm" data-testid="payables-record-supplier" />
            </Field>
            <Field label="Their email (optional)">
              <Input value={form.supplier_email} onChange={(e) => set("supplier_email", e.target.value)}
                className="h-9 text-sm" data-testid="payables-record-email" />
            </Field>
          </div>

          {isInvoice ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Invoice #">
                  <Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)}
                    className="h-9 text-sm font-mono" data-testid="payables-record-invoice-number" />
                </Field>
                <Field label="Invoice date">
                  <Input type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-invoice-date" />
                </Field>
                <Field label="Due date">
                  <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-due-date" />
                </Field>
                <Field label="Amount">
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-amount" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Terms">
                  <Input value={form.terms} onChange={(e) => set("terms", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-terms" />
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={(e) => set("status", e.target.value)}
                    className="w-full h-9 text-sm border border-slate-300 rounded-md px-2"
                    data-testid="payables-record-status">
                    {["received", "approved", "partially_paid", "paid", "disputed", "void",
                      ...(["pending_approval", "rejected"].includes(form.status) ? [form.status] : [])
                    ].map(s =>
                      <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </Field>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Method">
                  <select value={form.method} onChange={(e) => set("method", e.target.value)}
                    className="w-full h-9 text-sm border border-slate-300 rounded-md px-2"
                    data-testid="payables-record-method">
                    {METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </Field>
                <Field label="Paid on">
                  <Input type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-paid-date" />
                </Field>
                <Field label="Amount">
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-amount" />
                </Field>
                <Field label="Reference / check #">
                  <Input value={form.reference} onChange={(e) => set("reference", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-reference" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bank">
                  <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)}
                    className="h-9 text-sm" data-testid="payables-record-bank" />
                </Field>
                <Field label="Account last 4">
                  <Input value={form.account_last4} onChange={(e) => set("account_last4", e.target.value)}
                    className="h-9 text-sm" maxLength={4} data-testid="payables-record-last4" />
                </Field>
              </div>
              <Field label="Against which invoice" hint="Leave blank when no supplier invoice was ever keyed.">
                <select value={form.invoice_id} onChange={(e) => set("invoice_id", e.target.value)}
                  className="w-full h-9 text-sm border border-slate-300 rounded-md px-2"
                  data-testid="payables-record-invoice-pick">
                  <option value="">No invoice on file</option>
                  {openInvoices.map(i => (
                    <option key={i.id} value={i.id}>
                      {i.supplier_name} · #{i.invoice_number} · ${i.balance ?? i.amount} open
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}

          <Field label="Order / work order ID" hint="Blank keeps it unlinked — you can back-link it any time.">
            <Input value={form.link_id} onChange={(e) => set("link_id", e.target.value)}
              placeholder="e.g. DO-C06C9FEE"
              className="h-9 text-sm font-mono" data-testid="payables-record-link" />
          </Field>

          <Field label="Notes / reference">
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
              className="text-sm" data-testid="payables-record-notes" />
          </Field>

          <div className="rounded-md border border-cyan-200 bg-cyan-50/60 px-3 py-2.5">
            <input type="file" ref={fileInput} className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.xlsx"
              data-testid="payables-record-file-input" />
            <Button type="button" size="sm" variant="outline" onClick={() => fileInput.current?.click()}
              className="h-7 text-[11px] gap-1.5 bg-white" data-testid="payables-record-attach">
              <Upload className="w-3 h-3" /> Attach {isInvoice ? "invoice PDF" : "bank / check confirmation"}
            </Button>
            {file && (
              <p className="text-[11px] text-cyan-900 mt-1.5 inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> {file.name}
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="payables-record-cancel">Cancel</Button>
          <Button size="sm" onClick={() => save(false)} disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="payables-record-save">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {editing ? "Save changes" : `Record ${isInvoice ? "invoice" : "payment"}`}
          </Button>
        </div>

        {/* Duplicate guard — the whole point of the ledger is not paying twice */}
        {duplicate && (
          <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
            onClick={() => setDuplicate(null)}>
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()} data-testid="payables-duplicate-warning">
              <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-black text-slate-900">
                    {duplicate.code === "duplicate_invoice" ? "This invoice is already on file"
                      : duplicate.code === "already_paid" ? "This invoice is already paid in full"
                        : duplicate.code === "overpay" ? "That is more than the invoice still owes"
                          : "This payment looks like one you already recorded"}
                  </h4>
                  <p className="text-xs text-slate-600 mt-1">{duplicate.message}</p>
                </div>
              </div>
              <div className="px-5 py-4 text-xs text-slate-600">
                {duplicate.code === "overpay"
                  ? "Lower the amount, or record it anyway if you genuinely overpaid and expect a credit."
                  : "Nothing has been saved. Close this to fix the entry, or record it anyway if you are certain this is a separate transaction."}
              </div>
              <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setDuplicate(null)}
                  data-testid="payables-duplicate-cancel">
                  Let me fix it
                </Button>
                <Button size="sm" variant="outline" disabled={saving}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => { setDuplicate(null); save(true); }}
                  data-testid="payables-duplicate-force">
                  Record anyway
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayablesRecordDialog;
